import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpc, trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { SectionTitle } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { Textarea } from 'shared/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from 'shared/components/ui/tooltip'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { formatDayTime } from 'shared/lib/utils/date'

export type AwaitingDecision = RouterOutputs['storage']['AwaitingDecisions'][number]
type AnswerInput = RouterInputs['storage']['AnswerForPartner']
type AnswerResult = RouterOutputs['storage']['AnswerForPartner']
type Decision = AnswerInput['decision']

/** Support's answer for the partner: answered, or refused with its reason; throws on a failed call. */
export type AnswerForPartner = (input: AnswerInput) => Promise<AnswerResult>

const answerViaTrpc: AnswerForPartner = (input) => trpcClient.storage.AnswerForPartner.mutate(input)

/**
 * «Решения партнёров» (ADR-0033, ADR-0041; tickets 40, 43): the Parcels waiting for
 * their partner's decision — the storage expired (extend by how many days, return or
 * dispose), or found after it was declared lost (deliver or return; the answers the
 * server lists, `decisions`). Support enters the answer of any partner — one without
 * an API, or one that answered by telephone — in a dialog, always with a comment.
 */
export function StorageDecisions() {
	const { t } = useTranslate()
	const query = useQuery(trpc.storage.AwaitingDecisions.queryOptions())
	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle title={t('support.decisionsWaiting')} count={query.data?.length} hint={t('support.decisionsExplain')} />
			{query.isError ? (
				<p className='text-sm text-destructive'>{query.error.message}</p>
			) : query.isLoading ? (
				<p className='text-sm text-muted-foreground'>{t('support.loading')}</p>
			) : (
				<StorageDecisionTable items={query.data ?? []} answer={answerViaTrpc} onDone={() => query.refetch()} />
			)}
		</section>
	)
}

/**
 * The waiting Parcels as one table, «Записать ответ» in each row opening the answer.
 * The answer is injected (`answer`) so the table is driven at its props in `bun test`.
 */
export function StorageDecisionTable({
	items,
	answer,
	onDone,
}: {
	items: AwaitingDecision[]
	answer: AnswerForPartner
	onDone: () => void
}) {
	const { t } = useTranslate()
	const [answering, setAnswering] = useState<AwaitingDecision | null>(null)

	const columns = useMemo<ColumnDef<AwaitingDecision>[]>(
		() => [
			{
				id: 'code',
				header: t('support.colParcel'),
				cell: ({ row: { original: d } }) => <span className='font-mono'>{d.trackingCode}</span>,
			},
			{
				id: 'kind',
				header: t('support.colKind'),
				cell: ({ row: { original: d } }) => (
					<Badge variant='outline' data-testid='storage-decision-kind'>
						{d.kind === 'found' && d.foundWhere
							? t('support.decisionKindFound', { where: t(`foundWhere.${d.foundWhere}`) })
							: t('support.decisionKindExpired')}
					</Badge>
				),
			},
			{
				id: 'partner',
				header: t('support.colPartner'),
				cell: ({ row: { original: d } }) => (
					<div className='flex items-center gap-2'>
						<span>{d.partnerName}</span>
						{d.partnerAnswersByApi && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Badge variant='secondary' data-testid='storage-decision-by-api'>
										{t('support.decisionByApiShort')}
									</Badge>
								</TooltipTrigger>
								<TooltipContent>{t('support.decisionByApi')}</TooltipContent>
							</Tooltip>
						)}
					</div>
				),
			},
			{ id: 'point', header: t('support.destination'), cell: ({ row: { original: d } }) => d.pointCode },
			{
				id: 'answerBy',
				header: t('support.colAnswerBy'),
				cell: ({ row: { original: d } }) => (
					<span className='tabular-nums' data-testid='storage-decision-answer-by'>
						{formatDayTime(d.answerBy)}
					</span>
				),
			},
			{
				id: 'actions',
				header: '',
				meta: { cellClassName: 'w-px text-right' },
				cell: ({ row: { original: d } }) => (
					<Button size='sm' variant='outline' onClick={() => setAnswering(d)} data-testid='storage-decision-open'>
						{t('support.decisionSubmit')}
					</Button>
				),
			},
		],
		[t],
	)

	const { table } = useDataTable({
		data: items,
		columns,
		rowCount: items.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		autoResetPageIndex: false,
		getRowId: (d) => `${d.parcelId}:${d.cycle}`,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<>
			<DataTable
				table={table}
				withPagination={false}
				emptyText={<span data-testid='storage-decisions-empty'>{t('support.decisionsEmpty')}</span>}
				row={{ attributes: (d) => ({ 'data-testid': `storage-decision-${d.parcelId}` }) }}
			/>
			<AnswerDialog item={answering} answer={answer} onClose={() => setAnswering(null)} onDone={onDone} />
		</>
	)
}

/**
 * The partner's answer for one Parcel: the decision, the days of an extension, the
 * comment. Refused here without a comment, and an extension without its days, as the
 * backend refuses them; the server's refusal in words, and the dialog stays.
 */
function AnswerDialog({
	item,
	answer,
	onClose,
	onDone,
}: {
	item: AwaitingDecision | null
	answer: AnswerForPartner
	onClose: () => void
	onDone: () => void
}) {
	const { t } = useTranslate()
	const [decision, setDecision] = useState<Decision | null>(null)
	const [days, setDays] = useState('')
	const [comment, setComment] = useState('')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [seeded, setSeeded] = useState<AwaitingDecision | null>(null)
	if (item !== seeded) {
		setSeeded(item)
		setDecision(null)
		setDays('')
		setComment('')
		setError(null)
	}

	const submit = async () => {
		if (!item || !decision || busy) return
		const text = comment.trim()
		if (!text) {
			setError(t('support.commentRequired'))
			return
		}
		const extendDays = Number(days)
		if (decision === 'extend' && !(Number.isInteger(extendDays) && extendDays > 0)) {
			setError(t('support.decisionNeedDays'))
			return
		}
		setBusy(true)
		setError(null)
		try {
			const result = await answer({
				parcelId: item.parcelId,
				kind: item.kind,
				cycle: item.cycle,
				decision,
				...(decision === 'extend' ? { extendDays } : {}),
				comment: text,
			})
			if (result.outcome === 'refused') {
				setError(t(`support.decisionRefused_${result.reason}`))
				return
			}
			onClose()
			onDone()
		} catch (err) {
			setError(t('support.actionFailed', { message: err instanceof Error ? err.message : String(err) }))
		} finally {
			setBusy(false)
		}
	}

	return (
		<Dialog open={item !== null} onOpenChange={(open) => !open && !busy && onClose()}>
			<DialogContent className='sm:max-w-lg' data-testid='storage-decision-dialog'>
				<DialogHeader>
					<DialogTitle>{t('support.decisionDialogTitle', { code: item?.trackingCode ?? '' })}</DialogTitle>
					<DialogDescription>
						{item &&
							[
								item.partnerName,
								item.pointCode,
								t(item.kind === 'found' ? 'support.decisionCycleFound' : 'support.decisionCycle', {
									cycle: String(item.cycle),
								}),
								t('support.decisionAnswerBy', { at: formatDayTime(item.answerBy) }),
							].join(' · ')}
					</DialogDescription>
				</DialogHeader>
				<div className='grid gap-4'>
					<div className='flex flex-wrap items-end gap-3'>
						<div role='radiogroup' className='inline-flex items-center gap-0.5 rounded-lg bg-muted p-0.5'>
							{item?.decisions.map((d) => (
								<Button
									key={d}
									role='radio'
									size='sm'
									variant={decision === d ? 'outline' : 'ghost'}
									aria-checked={decision === d}
									onClick={() => setDecision(d)}
									data-testid={`storage-decision-${d}`}
								>
									{t(`support.decision_${d}`)}
								</Button>
							))}
						</div>
						{decision === 'extend' && (
							<div className='grid gap-1.5'>
								<Label htmlFor='storage-decision-days'>{t('support.extendDays')}</Label>
								<Input
									id='storage-decision-days'
									className='w-32'
									type='number'
									min={1}
									value={days}
									onChange={(e) => setDays(e.target.value)}
									data-testid='storage-decision-days'
								/>
							</div>
						)}
					</div>
					<Textarea
						aria-label={t('support.comment')}
						placeholder={t('support.decisionCommentPlaceholder')}
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						data-testid='storage-decision-comment'
					/>
					{error && (
						<p role='alert' className='text-sm text-destructive' data-testid='storage-decision-error'>
							{error}
						</p>
					)}
				</div>
				<DialogFooter>
					<Button variant='outline' onClick={onClose} disabled={busy}>
						{t('support.cancel')}
					</Button>
					<Button onClick={submit} disabled={busy || !decision} data-testid='storage-decision-submit'>
						{t('support.decisionSubmit')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
